import type { Directive } from 'vue'

/**
 * v-fit-height — 让 el-table 的表体固定高度、内部滚动、随屏幕自适应。
 *
 * 用法: <el-table v-fit-height> ... </el-table>
 *       <el-table v-fit-height="40"> ... </el-table>   // 自定义底部留白 (默认 20px)
 *
 * 原理: 计算 el-table 表体 (.el-table__body-wrapper) 顶部到视口底的距离,
 *   设为 maxHeight + overflow-y:auto。表头 (.el-table__header-wrapper) 是同级元素,
 *   固定不动。mount / resize / 数据更新(updated) 时重算, 故随屏幕高度和内容变化自适应。
 *
 * 注意: 用了本指令的表格不要再传 :max-height 属性 (会互相覆盖)。
 */
interface FitEl extends HTMLElement {
  __fitApply?: () => void
  __fitIO?: IntersectionObserver
  __fitBar?: HTMLElement
  __fitBarCleanup?: () => void
}

function bodyScrollElement(el: FitEl): HTMLElement | null {
  return el.querySelector<HTMLElement>('.el-table__body-wrapper .el-scrollbar__wrap')
    || el.querySelector<HTMLElement>('.el-table__body-wrapper')
}

function syncHorizontalScroll(el: FitEl): void {
  const bar = el.__fitBar
  const content = bar?.firstElementChild as HTMLElement | null
  const body = bodyScrollElement(el)
  const table = el.querySelector<HTMLElement>('.el-table__body')
  if (!bar || !content || !body || !table) return

  content.style.width = `${Math.max(table.scrollWidth, body.clientWidth)}px`
  bar.style.visibility = body.scrollWidth > body.clientWidth ? 'visible' : 'hidden'
  body.style.overflowX = 'hidden'
  bar.scrollLeft = body.scrollLeft

  el.__fitBarCleanup?.()
  const onBodyScroll = () => { if (bar.scrollLeft !== body.scrollLeft) bar.scrollLeft = body.scrollLeft }
  const onBarScroll = () => { if (body.scrollLeft !== bar.scrollLeft) body.scrollLeft = bar.scrollLeft }
  body.addEventListener('scroll', onBodyScroll, { passive: true })
  bar.addEventListener('scroll', onBarScroll, { passive: true })
  el.__fitBarCleanup = () => {
    body.removeEventListener('scroll', onBodyScroll)
    bar.removeEventListener('scroll', onBarScroll)
  }
}

function apply(el: FitEl, margin: number): void {
  const wrap = el.querySelector<HTMLElement>('.el-table__body-wrapper')
  if (!wrap) return
  const top = wrap.getBoundingClientRect().top
  const barHeight = el.__fitBar?.offsetHeight || 14
  const h = Math.max(160, Math.floor(window.innerHeight - top - margin - barHeight))
  wrap.style.maxHeight = `${h}px`
  wrap.style.overflowY = 'auto'
  syncHorizontalScroll(el)
}

export const fitHeight: Directive<FitEl, number | undefined> = {
  mounted(el, binding) {
    const margin = typeof binding.value === 'number' ? binding.value : 20
    // 员工档案列表已有专用横向滚动条，避免重复渲染。
    const hasExternalBar = !!el.parentElement?.querySelector('.emp-table-x-scroll')
    if (!hasExternalBar) {
      const bar = document.createElement('div')
      bar.className = 'fit-height-hscroll'
      bar.setAttribute('aria-label', '表格横向滚动条')
      const content = document.createElement('div')
      content.className = 'fit-height-hscroll-content'
      bar.appendChild(content)
      el.parentElement?.insertBefore(bar, el.nextSibling)
      el.__fitBar = bar
    }
    const run = () => apply(el, margin)
    el.__fitApply = run
    // el-table 布局异步完成, 多次延迟应用确保测准
    requestAnimationFrame(run)
    setTimeout(run, 150)
    setTimeout(run, 400)
    window.addEventListener('resize', run)
    // 变可见时重算 (在非激活 tab 里 mounted 时 display:none 测不准, 切到该 tab 才有尺寸)
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) run()
      })
      io.observe(el)
      el.__fitIO = io
    }
  },
  updated(el) {
    // 数据变化 / 切 tab 后 el-table 重排, 重新贴合
    el.__fitApply?.()
  },
  unmounted(el) {
    if (el.__fitApply) window.removeEventListener('resize', el.__fitApply)
    el.__fitIO?.disconnect()
    el.__fitBarCleanup?.()
    el.__fitBar?.remove()
  },
}
